"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tooltip,
  IconButton,
  HStack,
  Text,
  FormErrorMessage,
  useToast,
} from "@chakra-ui/react";
import { Info } from "lucide-react";
import { IoKeypadOutline } from "react-icons/io5";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface PressDigitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: any) => void;
  initialValues?: any;
}

export const PressDigitModal: React.FC<PressDigitModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialValues,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [delayMs, setDelayMs] = useState<number>(1000);
  const [nameError, setNameError] = useState("");

  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";
  const toast = useToast();

  useEffect(() => {
    if (initialValues) {
      setName(initialValues.name || "press_digit");
      setDescription(
        initialValues.description ||
          "Navigate to the human agent of sales department"
      );
      setDelayMs(initialValues.delay_ms || 1000);
    } else {
      // Reset to defaults when adding a new function
      setName("press_digit");
      setDescription("Navigate to the human agent of sales department");
      setDelayMs(1000);
    }
  }, [initialValues]);

  const validateToolName = (name: string) => {
    if (!name.trim()) {
      setNameError("Tool name cannot be empty");
      return false;
    }

    if (name.length > 64) {
      setNameError("Tool name must be 64 characters or less");
      return false;
    }

    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validNameRegex.test(name)) {
      setNameError(
        "Tool name must only contain letters, numbers, underscores, and dashes (no spaces)"
      );
      return false;
    }

    setNameError("");
    return true;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameError("");
  };

  const handleSave = () => {
    if (!validateToolName(name)) {
      toast({
        title: "Invalid tool name",
        description: nameError,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const config = {
      type: "press_digit",
      name,
      description,
      delay_ms: delayMs,
    };
    onSave(config);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent
        bg={isDark ? "gray.800" : "white"}
        textColor={isDark ? "white" : "gray.800"}
      >
        <ModalHeader style={{ display: "flex", alignItems: "center" }}>
          <IoKeypadOutline size={20} style={{ marginRight: "8px" }} />
          <span>Press Digit (IVR Navigation)</span>
        </ModalHeader>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired isInvalid={!!nameError}>
              <FormLabel color={isDark ? "gray.300" : "gray.700"}>
                Name
              </FormLabel>
              <Input
                id="press-digit-function-name"
                value={name}
                onChange={handleNameChange}
                onBlur={() => validateToolName(name)}
                placeholder="Enter function name"
                _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                textColor={isDark ? "white" : "gray.800"}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                variant="outline"
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
              />
              {nameError && <FormErrorMessage>{nameError}</FormErrorMessage>}
            </FormControl>

            <FormControl>
              <FormLabel color={isDark ? "gray.300" : "gray.700"}>
                Description (Optional)
              </FormLabel>
              <Textarea
                sx={
                  isDark
                    ? {
                        "&::-webkit-scrollbar": {
                          height: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#2D3748", // Dark track color
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#4A5568", // Dark thumb color
                          borderRadius: "2px",
                        },
                        // For Firefox
                        scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                        _hover: {
                          scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                        },
                      }
                    : {
                        "&::-webkit-scrollbar": {
                          height: "4px",
                        },
                        "&::-webkit-scrollbar-button": {
                          display: "none",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#fff",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#fff",
                          borderRadius: "2px",
                        },
                        scrollbarColor: "rgb(220, 220, 220) white",
                        _hover: {
                          scrollbarColor: "rgb(220, 220, 220) white",
                        },
                      }
                }
                id="press-digit-function-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter function description"
                _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                textColor={isDark ? "white" : "gray.800"}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                variant="outline"
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
              />
            </FormControl>

            <FormControl>
              <HStack align="center">
                <FormLabel mb={0} color={isDark ? "gray.300" : "gray.700"}>
                  Pause Detection Delay (ms)
                </FormLabel>
                <Tooltip label="Adds extra wait time after pauses to prevent pressing too early. Ensures digits are pressed only after the IVR fully finishes. Default: 1000ms">
                  <IconButton
                    marginLeft={-5}
                    marginTop={0.5}
                    aria-label="Pause detection delay info"
                    icon={<Info size={14} />}
                    size="xs"
                    variant="ghost"
                    color={isDark ? "gray.300" : "gray.700"}
                  />
                </Tooltip>
              </HStack>
              <NumberInput
                id="press-digit-function-delay"
                value={delayMs}
                onChange={(_, valueNumber) =>
                  setDelayMs(Math.min(Math.max(valueNumber, 0), 5000))
                }
                min={0}
                max={5000}
                step={100}
              >
                <NumberInputField
                  placeholder="Enter delay in milliseconds"
                  _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                  textColor={isDark ? "white" : "gray.800"}
                  bg={isDark ? "gray.700" : "white"}
                  color={isDark ? "white" : "gray.800"}
                  borderColor={isDark ? "gray.600" : "gray.300"}
                  _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                  variant="outline"
                />
                <NumberInputStepper>
                  <NumberIncrementStepper
                    color={isDark ? "white" : "gray.800"}
                  />
                  <NumberDecrementStepper
                    color={isDark ? "white" : "gray.800"}
                  />
                </NumberInputStepper>
              </NumberInput>
              <Text
                fontSize="sm"
                color={isDark ? "gray.400" : "gray.600"}
                mt={1}
              >
                Valid range: 0-5000ms
              </Text>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter
          borderTop="1px solid"
          borderColor={isDark ? "gray.700" : "gray.200"}
        >
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            colorScheme={isDark ? "black" : "white"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
          >
            Cancel
          </Button>
          <Button
            colorScheme="gray"
            onClick={handleSave}
            _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
            background={isDark ? "gray.700" : "gray.200"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
